import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSubject } from "../../context/SubjectContext";
import "./MarkSubmissionViewHeader.css";
import { useModerationWindowSession } from "../../constants/Constants";
import { useCenter } from "../../context/CenterContext";
import { handleDownload, getCenterType, checkunitIsConsortiaUnit } from "../../utils/utils";
import toast from "react-hot-toast";
import { useApi } from "../../context/ApiClient";
import { useConsortiaCenter } from "../../context/ConsortiaCenterContext";

const MarkSubmissionViewHeader = ({ rows, filenamePrefix }) => {
  const moderationWindowSession = useModerationWindowSession();
  const navigate = useNavigate();
  const { subjectData } = useSubject();
  const { selectedSubject, selectedSubjectId } = subjectData || {};
  const { centerNumber, centerName , centres} = useCenter();
  const api = useApi(process.env.REACT_APP_AMS_BASE_URL);
  const { consortiaCentres = [] } = useConsortiaCenter();
  const currentCentreType = getCenterType(consortiaCentres, Number(centerNumber), selectedSubject, selectedSubjectId) || "";




  const location = useLocation();
  const {
    unit,
    sessionId,
    sessionStartDate,
    sessionEndDate,
    visitDate,
    statusType,
    stage, // optional, pass from navigate state if you have it
  } = location.state || {};

  const consortiaUnitcode = String(unit ?? "")
    .toUpperCase()
    .match(/^[A-Z0-9]+/)?.[0] || "";
  const isConsortiaUnit = checkunitIsConsortiaUnit(
    consortiaCentres,
    centerNumber,
    sessionId,
    consortiaUnitcode,
  );
  const [editLoading, setEditLoading] = useState(false);
  // REMOVE persistent editError state render; we can keep state if needed for debugging, but we won’t render it
  const [editError, setEditError] = useState(null);

  const assessmentCode = rows?.[0]?.assessmentCode;
  const assessmentName = rows?.[0]?.assessmentName;

  const getSessionDetails = (sessionStartDate, sessionEndDate, sessionId) => {
    const academicYear = `${new Date(sessionStartDate).getFullYear()}/${String(
      new Date(sessionEndDate).getFullYear()
    ).slice(-2)}`;
    return sessionId === moderationWindowSession.window1
      ? `Window 1 (February series) - ${academicYear}`
      : sessionId === moderationWindowSession.window2
        ? `Window 2 (June series) - ${academicYear}`
        : "";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (isNaN(date)) return "";
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // If any candidate is sampled, sampling has run → hide Edit and show advisory text
  const samplingHasRun = useMemo(
    () => (rows || []).some((r) => r.isSampled === true),
    [rows]
  );

  const isStageEditable = (s) => s !== 3;
  const effectiveStage = stage ?? 2;
   let isRestConsortiaUnit = currentCentreType === "Rest" && isConsortiaUnit;
  const shouldShowEditButton = !samplingHasRun && isStageEditable(effectiveStage) && !isRestConsortiaUnit ;

  const toSlug = (str) => {
    const lower = (str || "").toLowerCase();
    const collapsed = lower.replace(/[^a-z0-9]+/g, "_");
    // Trim leading/trailing underscores without alternation
    const trimmedStart = collapsed.replace(/^_+/, "");
    return trimmedStart.replace(/_+$/, "");
  };

  const handleBack = () => navigate(-1);

  const handleEdit = async () => {
    if (!assessmentCode || !centerNumber || !sessionId) return;
    setEditLoading(true);
    setEditError(null);
    try {
      const url = `${process.env.REACT_APP_AMS_BASE_URL}editTotalMarkSubmission?assessmentCode=${encodeURIComponent(
        assessmentCode
      )}&centreNumber=${encodeURIComponent(
        centerNumber
      )}&sessionId=${encodeURIComponent(sessionId)}`;

      const res = await api.get(url);
      const enabled =
        res?.data?.isSuccess && res?.data?.data?.editTotalMarks === "enabled";

      if (enabled) {
        const unitSlug = toSlug(
          `${assessmentCode} ${assessmentName || unit || ""}`.trim()
        );
        navigate(`/dashboard/marks_submission/${selectedSubject}/${unitSlug}`, {
          state: {
            unit: `${assessmentCode} ${assessmentName}`,
            sessionId,
            sessionStartDate,
            sessionEndDate,
            visitDate,
            statusType,
            fromEdit: true,
            stage: effectiveStage,
          },
        });
      } else {
        const msg =
          res?.data?.message ||
          "Edit not available. If you need to change marks, please contact OCR.";
        setEditError(msg); // keep for debugging if needed
        // SHOW TOAST instead of inline banner
        toast.error(msg);
      }
    } catch (e) {
      const msg = "Failed to enable editing. Please try again.";
      setEditError(msg); // keep for debugging
      toast.error(msg);  // SHOW TOAST
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <>
      <hr className="mb-0 mt-2 view-divider" />
      <div
        className="xcard unit-header xp-4 xhas-shadow-2 is-flex is-justify-content-space-between mb-1 is-align-items-center"
        style={{ backgroundColor: "#FFFFFF" }}
      >
        <div style={{ display: "flex", flexDirection: "column", textAlign: "left" }}>
          <div className="is-size-6">
            {(["Visit Confirmed", "Visit Completed"].includes(statusType) ) && (
              <>
                <strong>Visit date: {formatDate(visitDate)}</strong> |{" "}
              </>
            )}
            {statusType === "Visit Requested" && (
              <>
                <strong>Visit requested</strong> |{" "}
              </>
            )}
            {getSessionDetails(sessionStartDate, sessionEndDate, sessionId)}
          </div>
          <div className="is-size-5">
            <strong>
              {assessmentCode && assessmentName
                ? `${assessmentCode} ${assessmentName}`
                : "Visit mark view"}
            </strong>
          </div>
        </div>

        <div>
          <div className="field is-grouped mt-3">
            <div className="tags has-addons mr-0">
              <span className="tag is-success view-submit-color mt-3">
                ✔ Total marks submitted
              </span>
            </div>

            <button className="view-back-btn mr-4 mt-1" onClick={handleBack}>
              <span className="icon">
                <svg
                  style={{ fill: "#0076BD" }}
                  xmlns="http://www.w3.org/2000/svg"
                  height="24"
                  viewBox="0 -960 960 960"
                  width="24"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path d="M560-253.847 333.847-480 560-706.153 602.153-664l-184 184 184 184L560-253.847Z"></path>
                </svg>
              </span>
              Back
            </button>

            <button
              className="view-download-btn mr-2 mt-1"
              onClick={() =>
                handleDownload(
                  rows,
                  "totalMarks",
                  location,
                  { centerNumber, centerName },
                  subjectData,
                  api,
                  {},
                  currentCentreType,
                  isConsortiaUnit,
                  centres,
                  moderationWindowSession
                )
              }
            >
              Download
            </button>

            {shouldShowEditButton && (
              <button
                className="view-download-btn mr-2 mt-1"
                type="button"
                disabled={editLoading}
                onClick={handleEdit}
                style={{ whiteSpace: "nowrap" }}
              >
                Edit
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Advisory panel when sampling has run */}
      {samplingHasRun && (
        <div className="mt-0 mb-4" data-instruction-panel="">
          <div className="notification is-info is-flex">
            <span className="icon mr-4">
              <svg
                focusable="false"
                aria-hidden="true"
                viewBox="0 0 24 24"
                style={{ width: 24, height: 24 }}
              >
                <path d="M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"></path>
              </svg>
            </span>
            <span>
              If you need to edit your total marks submission at this stage, please contact Cambridge OCR.
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export default MarkSubmissionViewHeader;